import type { CombinedError } from 'urql';

export function checkIfAwsWafChallengeError(error?: CombinedError) {
  if (!error) return false;

  const networkErrorResponse =
    error.networkError && 'response' in error.networkError
      ? error.networkError.response
      : undefined;

  const action =
    getAwsWafAction(error.response) ??
    getAwsWafAction(error.networkError) ??
    getAwsWafAction(networkErrorResponse);

  return action?.trim().toLowerCase() === AWS_WAF_CHALLENGE_ACTION;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAwsWafAction(response: unknown): string | undefined {
  if (!isResponseLike(response)) return undefined;

  return getHeader(response.headers, AWS_WAF_ACTION_HEADER);
}

function getHeader(headers: HeadersLike | undefined, name: string): string | undefined {
  if (!headers) return undefined;

  if (hasGetHeader(headers)) {
    return headers.get(name) ?? undefined;
  }

  const lowerName = name.toLowerCase();

  return Object.entries(headers.map ?? {}).find(([key]) => key.toLowerCase() === lowerName)?.[1];
}

function isResponseLike(value: unknown): value is ResponseLike {
  return !!value && typeof value === 'object' && 'headers' in value;
}

function hasGetHeader(headers: HeadersLike): headers is Headers {
  return 'get' in headers && typeof headers.get === 'function';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const AWS_WAF_ACTION_HEADER = 'x-amzn-waf-action';
const AWS_WAF_CHALLENGE_ACTION = 'challenge';

// ─── Types ───────────────────────────────────────────────────────────────────

type SerializedHeaders = {
  map?: Record<string, string>;
};

type HeadersLike = Headers | SerializedHeaders;

type ResponseLike = {
  headers?: HeadersLike;
};
